import { FC } from 'react';
import { Truck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface OrderSummaryProps {
    subtotal: number;
    discount?: number;
    shippingCost?: number | null; // null means calculated at checkout
    isFreeShipping?: boolean;
    freeShippingThreshold?: number | null;
}

export const OrderSummary: FC<OrderSummaryProps> = ({
    subtotal,
    discount = 0,
    shippingCost = null,
    isFreeShipping = false,
    freeShippingThreshold = null
}) => {
    const { t } = useTranslation('shop');

    // Free Shipping Progress calculation
    let progressPercentage = 0;
    let amountNeeded = 0;

    if (freeShippingThreshold) {
        progressPercentage = Math.min((subtotal / freeShippingThreshold) * 100, 100);
        amountNeeded = Math.max(freeShippingThreshold - subtotal, 0);
    }

    const total = subtotal - discount + (shippingCost || 0);

    return (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 flex flex-col gap-6 w-full">
            <h3 className="font-bold text-lg text-secondary">Order Summary</h3>

            {/* Free Shipping Nudge */}
            {freeShippingThreshold !== null && !isFreeShipping && amountNeeded > 0 && (
                <div className="bg-primary/5 border border-primary/10 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-1.5 bg-primary/10 text-primary rounded-full">
                            <Truck size={16} />
                        </div>
                        <p className="text-sm font-medium text-gray-700 font-bold">
                            Add <span className="text-primary font-bold">EGP {amountNeeded.toLocaleString()}</span> more to get <span className="font-bold">Free Shipping!</span>
                        </p>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                            className="bg-primary h-1.5 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${progressPercentage}%` }}
                        ></div>
                    </div>
                </div>
            )}

            {isFreeShipping && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 flex items-center gap-3 text-emerald-700">
                    <Truck size={18} />
                    <span className="text-sm font-bold">You've unlocked Free Shipping!</span>
                </div>
            )}

            {/* Breakdown */}
            <div className="flex flex-col gap-3 text-sm text-gray-600">
                <div className="flex justify-between items-center">
                    <span>{t('cart.subtotal')}</span>
                    <span className="font-medium text-gray-900">EGP {subtotal.toLocaleString()}</span>
                </div>

                {discount > 0 && (
                    <div className="flex justify-between items-center text-emerald-600">
                        <span>Discount</span>
                        <span className="font-bold">−EGP {discount.toLocaleString()}</span>
                    </div>
                )}

                <div className="flex justify-between items-center">
                    <span>{t('cart.shipping')}</span>
                    {isFreeShipping ? (
                        <span className="font-bold text-emerald-600">Free</span>
                    ) : shippingCost === null ? (
                        <span className="text-gray-400 text-xs text-right whitespace-pre-wrap leading-tight">{t('cart.calculatedAtCheckout')}</span>
                    ) : (
                        <span className="font-medium text-gray-900">EGP {shippingCost.toLocaleString()}</span>
                    )}
                </div>
            </div>

            <div className="w-full h-px bg-gray-100"></div>

            {/* Total */}
            <div className="flex justify-between items-end">
                <span className="font-bold text-gray-900">{t('cart.total')}</span>
                <div className="text-right">
                    <span className="font-bold text-xl lg:text-2xl text-primary">
                        EGP {total.toLocaleString()}
                    </span>
                    <div className="text-xs text-gray-400 mt-1">{t('cart.includingVat')}</div>
                </div>
            </div>
        </div>
    );
};
